import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SelectOption from '../../components/Pathfinder/option/SelectOption';
import PathfinderBottomToolbar from '../../components/Pathfinder/layout/PathfinderBottomToolbar';
import PathfinderQuestionHeader from '../../components/Pathfinder/layout/PathfinderQuestionHeader';
import PathfinderTextInput from '../../components/Pathfinder/input/PathfinderTextInput';
import PathfinderSingleSelectDropdown from '../../components/Pathfinder/dropdown/PathfinderSingleSelectDropdown';
import PathfinderLangScorePicker from '../../components/Pathfinder/dropdown/PathfinderLangScorePicker';
import PathfinderMajorSearchSelect from '../../components/Pathfinder/search/PathfinderMajorSearchSelect';
import PathfinderUniversitySlotSelect from '../../components/Pathfinder/search/PathfinderUniversitySlotSelect';
import PathfinderToolSearchSelect from '../../components/Pathfinder/search/PathfinderToolSearchSelect';
import PathfinderSchoolMasterSearchSelect from '../../components/Pathfinder/search/PathfinderSchoolMasterSearchSelect';
import PathfinderExpBlockFlow from '../../components/Pathfinder/expblock/PathfinderExpBlockFlow';
import PathfinderTimelineFlow from '../../components/Pathfinder/expblock/PathfinderTimelineFlow';
import PathfinderMobileExpBlockFlow from '../../components/Pathfinder/mobile/PathfinderMobileExpBlockFlow';
import PathfinderMobileQuestionInput from '../../components/Pathfinder/mobile/PathfinderMobileQuestionInput';
import PathfinderMobileQuestionLayout from '../../components/Pathfinder/mobile/PathfinderMobileQuestionLayout';
import PathfinderMobileTimelineFlow from '../../components/Pathfinder/mobile/PathfinderMobileTimelineFlow';
import PathfinderSelectCardGroup from '../../components/Pathfinder/option/PathfinderSelectCardGroup';
import PathfinderMultiSelectCardGrid from '../../components/Pathfinder/option/PathfinderMultiSelectCardGrid';
import PathfinderResult from './PathfinderResult';
import {
  KOREAN_DEGREE_FOLLOW_UP_CODES,
  isQuestionAnswered,
  resolveNextQuestionStep,
  shouldHideKoreanDegreeFollowUp,
  shouldHideRoleExploration,
} from './pathfinderNavigation';
import {
  createPathfinderSession,
  getPathfinderQuestions,
  savePathfinderSection,
  savePathfinderExperiences,
  evaluatePathfinderSession,
} from '../../api/pathfinder/pathfinder.api';
import {
  trackPathfinderOnboardingComplete,
  useAuth,
  useIsMobile,
  useLoginRequiredModal,
} from '@repo/common';

// 어드민 권한 판별용 role 값 (MinimalGnb와 동일 기준)
const ADMIN_ROLES = ['A', 'ROLE_A', 'ADMIN', 'ROLE_ADMIN'];

// ─── Goal type: FE option id → BE enum 매핑 ────────────────────────────────
const GOAL_TYPE_BE_MAP = {
  GOAL_JOB: 'JOB',
  GOAL_GRAD: 'GRAD',
  GOAL_UG: 'UG',
  GOAL_TRANSFER: 'TRANSFER',
  GOAL_LANGUAGE: 'LANGUAGE_STUDY',
};

const QUESTION_ORDER_BY_GOAL_TYPE = {
  UG: [
    'Q_TARGET_MAJOR_FIELD',
    'Q_TARGET_SCHOOL_LIST',
    'Q_GOAL_TIMELINE_DEFINED',
    'Q_STAY_INTENT',
    'Q_EXP_BLOCK',
    'Q_HS_DOC_SIMPLIFIED',
    'Q_LANG_SCORE',
    'Q_NATIONALITY_CHECK',
    'Q_PORTFOLIO_AWARD',
    'Q_PORTFOLIO_LANG',
    'Q_OPTIONAL_CAREER_LINK',
    'Q_ROLE_EXPLORATION',
  ],
  GRAD: [
    'Q_TARGET_MAJOR_FIELD',
    'Q_TARGET_SCHOOL_LIST',
    'Q_GOAL_TIMELINE_DEFINED',
    'Q_STAY_INTENT',
    'Q_CURRENT_MAJOR_FIELD',
    'Q_EXP_BLOCK',
    'Q_LANG_SCORE',
    'Q_NATIONALITY_CHECK',
    'Q_PORTFOLIO_AWARD',
    'Q_PORTFOLIO_LANG',
    'Q_OPTIONAL_CAREER_LINK',
    'Q_ROLE_EXPLORATION',
  ],
  TRANSFER: [
    'Q_TARGET_MAJOR_FIELD',
    'Q_TARGET_SCHOOL_LIST',
    'Q_GOAL_TIMELINE_DEFINED',
    'Q_STAY_INTENT',
    'Q_CURRENT_MAJOR_FIELD',
    'Q_EXP_BLOCK',
    'Q_LANG_SCORE',
    'Q_NATIONALITY_CHECK',
    'Q_PORTFOLIO_AWARD',
    'Q_PORTFOLIO_LANG',
    'Q_OPTIONAL_CAREER_LINK',
    'Q_ROLE_EXPLORATION',
  ],
  LANGUAGE_STUDY: [
    'Q_LANG_PURPOSE',
    'Q_LANG_CURRENT_LEVEL',
    'Q_LANG_TARGET_LEVEL',
    'Q_LANG_USE_CONTEXT',
    'Q_MAJOR_LINK',
    'Q_JOB_ROLE',
    'Q_STAY_INTENT',
    'Q_TIMELINE_DEFINED',
  ],
};

const orderQuestionsByGoalType = (questions, goalType) => {
  const orderedCodes = QUESTION_ORDER_BY_GOAL_TYPE[goalType];
  if (!orderedCodes?.length) return questions;

  const indexByCode = new Map(orderedCodes.map((code, index) => [code, index]));

  return questions
    .filter((question) => indexByCode.has(question.questionCode))
    .sort((left, right) => {
      const leftIndex = indexByCode.get(left.questionCode);
      const rightIndex = indexByCode.get(right.questionCode);

      return leftIndex - rightIndex;
    });
};

const PATHFINDER_DRAFT_STORAGE_KEY = 'pathfinderDraft';
const READINESS_SCORE_STORAGE_KEY = 'pathfinderReadinessScore';
const READINESS_STAGE_STORAGE_KEY = 'pathfinderReadinessStage';

const getPathfinderDraft = () => {
  try {
    const draft = JSON.parse(
      localStorage.getItem(PATHFINDER_DRAFT_STORAGE_KEY) || 'null',
    );

    return draft && typeof draft === 'object' ? draft : {};
  } catch {
    return {};
  }
};

// Retake 시 저장된 설문 진행 상태(선택지)와 점수를 모두 비운다.
export const clearPathfinderDraft = () => {
  localStorage.removeItem(PATHFINDER_DRAFT_STORAGE_KEY);
  localStorage.removeItem(READINESS_SCORE_STORAGE_KEY);
};

// ─── Q1: goal_type (FE 고정, BE DB에도 등록됨) ─────────────────────────────
const GOAL_TYPE_QUESTION = {
  questionCode: 'Q_GOAL_TYPE',
  sectionCode: 'C1',
  prompt: 'What do you want to do in Korea?',
  helperText: 'Select your primary goal',
  answerType: 'SINGLE_SELECT',
  configJson: null,
  options: [
    {
      optionCode: 'GOAL_JOB',
      optionLabel: 'Get a Job in Korea',
      description: 'Start or continue your career in Korea',
    },
    {
      optionCode: 'GOAL_GRAD',
      optionLabel: 'Graduate School (Master/PhD)',
      description: "Pursue a master's or doctoral degree",
    },
    {
      optionCode: 'GOAL_UG',
      optionLabel: 'Undergraduate Degree (BA)',
      description: "Enroll in a bachelor's degree program",
    },
    {
      optionCode: 'GOAL_TRANSFER',
      optionLabel: 'Transfer to a Korean University',
      description: 'Continue your degree at a Korean university',
    },
    {
      optionCode: 'GOAL_LANGUAGE',
      optionLabel: 'Language Program',
      description: 'Study Korean at a language institute',
    },
  ],
};

// ─── configJson 파싱 헬퍼 ───────────────────────────────────────────────────
const parseConfig = (configJson) => {
  if (!configJson) return {};
  try {
    return typeof configJson === 'string' ? JSON.parse(configJson) : configJson;
  } catch {
    return {};
  }
};

// ─── 옵션 필터링: dependsOnMap 질문에서 이전 답변으로 필터 ─────────────────
// [boolean 방식] optionValue = "PARENT_A,PARENT_B" 형태로 부모 코드 목록 저장
// [object 방식]  dependsOnMap = { parentCode: [allowedOptionCodes...] }
const filterOptionsByParent = (options, parentAnswer, dependsOnMap) => {
  if (!parentAnswer) return options;

  // object 방식: dependsOnMap이 객체면 map[parentAnswer]로 허용 코드 목록 조회
  if (
    dependsOnMap &&
    typeof dependsOnMap === 'object' &&
    dependsOnMap !== true
  ) {
    const allowed = dependsOnMap[parentAnswer];
    if (!allowed) return [];
    const allowedSet = new Set(allowed);
    return options.filter((opt) => allowedSet.has(opt.optionCode));
  }

  // boolean 방식: optionValue에 부모 코드 쉼표 구분 목록
  const parentCodes = Array.isArray(parentAnswer)
    ? parentAnswer
    : [parentAnswer];
  return options.filter((opt) => {
    if (!opt.optionValue) return true;
    const allowedParents = opt.optionValue.split(',').map((v) => v.trim());
    return parentCodes.some((code) => allowedParents.includes(code));
  });
};

// ─── BE options → SelectOption 형식 변환 ───────────────────────────────────
const toSelectOptions = (options) =>
  options.map((opt) => ({
    id: opt.optionCode,
    title: opt.optionLabel,
    caption: opt.description ?? '',
  }));

// ─── BE options → Dropdown 형식 변환 ───────────────────────────────────────
const toDropdownOptions = (options) =>
  options.map((opt) => ({
    value: opt.optionCode,
    label: opt.optionLabel,
  }));

const ROLE_GRID_QUESTION_CODES = new Set(['Q_JOB_ROLE', 'Q_JOB_INDUSTRY']);
const VISA_GRID_QUESTION_CODES = new Set(['Q_GOAL_DETAIL', 'Q_PORTFOLIO_LANG']);
const PORTFOLIO_AWARD_OPTION_CODES = new Set(['PORT_YES', 'PORT_NO']);
const MAJOR_SEARCH_GOAL_CODES = new Set([
  'GOAL_UG',
  'GOAL_GRAD',
  'GOAL_TRANSFER',
]);
const EXP_BLOCK_DURATION_SKIP_GOAL_CODES = new Set([
  'GOAL_UG',
  'GOAL_TRANSFER',
  'GOAL_GRAD',
  'GOAL_LANGUAGE',
]);
// Q_JOB_ROLE에서 Other(ROLE_etc) 선택 시 건너뛰는 질문 코드
// - Q_JOB_INDUSTRY: Other(IND_etc)로 자동 선택 처리하므로 노출하지 않음
// - Q_ROLE_TASKS / Q_ROLE_SUBFUNCTION: ROLE_etc에 매칭되는 옵션이 없어 노출하지 않음
const ROLE_ETC_SKIP_QUESTION_CODES = new Set([
  'Q_JOB_INDUSTRY',
  'Q_ROLE_TASKS',
  'Q_ROLE_SUBFUNCTION',
]);
const persistReadinessScore = (result) => {
  const score = result?.finalScore == null ? null : Number(result.finalScore);

  if (result?.finalStage) {
    localStorage.setItem(READINESS_STAGE_STORAGE_KEY, String(result.finalStage));
  }

  if (!Number.isFinite(score)) return;

  localStorage.setItem(
    READINESS_SCORE_STORAGE_KEY,
    String(Math.max(0, Math.min(100, score))),
  );
};

const ROLE_TASK_PREFIX_BY_ROLE = {
  ROLE_IT: 'TASK_IT_',
  ROLE_DESIGN: 'TASK_DS_',
  ROLE_BUSINESS: 'TASK_BIZ_',
  ROLE_MARKETING: 'TASK_MKT_',
  ROLE_MANUFACTURING: 'TASK_MFG_',
  ROLE_EDU: 'TASK_EDU_',
  ROLE_TRADE: 'TASK_TRADE_',
  ROLE_SALES: 'TASK_SALES_',
  ROLE_SERVICE: 'TASK_SVC_',
  ROLE_CONSTRUCTION: 'TASK_CON_',
  ROLE_ENTERTAINMENT: 'TASK_ENT_',
  ROLE_INTERPRETATION: 'TASK_INT_',
  'ROLE_R&D': 'TASK_RD_',
};

const filterRoleTaskOptions = (questionCode, options, roleCode) => {
  if (questionCode !== 'Q_ROLE_TASKS' || !roleCode) return options;

  const prefix = ROLE_TASK_PREFIX_BY_ROLE[roleCode];
  if (!prefix) return [];

  return options.filter((option) =>
    String(option.optionCode || '').startsWith(prefix),
  );
};

// ─── 질문 타입별 컴포넌트 렌더링 ────────────────────────────────────────────
function QuestionInput({ question, value, onChange, answers, answerLabelMap }) {
  const { answerType, options = [], configJson } = question;
  const config = parseConfig(configJson);
  const isDegreeGoalType = MAJOR_SEARCH_GOAL_CODES.has(answers.Q_GOAL_TYPE);
  const majorSearchPlaceholder =
    isDegreeGoalType && question.questionCode === 'Q_TARGET_MAJOR_FIELD'
      ? 'Search your target major (e.g., Business, Computer Science, Psychology)'
      : question.helperText || 'Search and select your major';

  // dependsOnMap: 이전 질문 답변으로 옵션 필터링
  const filteredOptions =
    config.dependsOnMap && config.dependsOn
      ? filterOptionsByParent(
          options,
          answers[config.dependsOn],
          config.dependsOnMap,
        )
      : options;

  const resolvedOptions =
    question.questionCode === 'Q_PORTFOLIO_AWARD'
      ? filteredOptions.filter((option) =>
          PORTFOLIO_AWARD_OPTION_CODES.has(option.optionCode),
        )
      : filterRoleTaskOptions(
          question.questionCode,
          filteredOptions,
          answers.Q_JOB_ROLE,
        );

  switch (answerType) {
    case 'LANG_SCORE_PICKER':
      // raw options 그대로 넘김 — description 필드로 cert/level 분리
      return (
        <PathfinderLangScorePicker
          options={resolvedOptions}
          value={value || {}}
          onChange={onChange}
        />
      );

    case 'DROPDOWN_SINGLE':
      return (
        <PathfinderSingleSelectDropdown
          options={toDropdownOptions(resolvedOptions)}
          value={value || ''}
          onChange={(val) => onChange(val)}
          placeholder='Select an option'
          visibleOptionCount={6}
          className='w-full'
        />
      );

    case 'SUMMARY_SEARCH':
      return (
        <PathfinderSchoolMasterSearchSelect
          value={value || null}
          onChange={onChange}
          selectedLabelValue={value ? answerLabelMap?.[value] : ''}
          placeholder={question.helperText || 'Search your high school'}
        />
      );

    case 'SEARCH_SELECT':
      if (config.searchType === 'KR_UNIVERSITY') {
        return (
          <PathfinderUniversitySlotSelect
            value={Array.isArray(value) ? value : []}
            onChange={onChange}
            placeholder={
              question.helperText ||
              'Search and add your target school (e.g. Yonsei University)'
            }
          />
        );
      }
      if (config.searchType === 'TOOL') {
        return (
          <PathfinderToolSearchSelect
            value={Array.isArray(value) ? value : []}
            onChange={onChange}
            placeholder={
              question.helperText || 'Search tools (e.g. Figma, Python, Excel)'
            }
          />
        );
      }
      return (
        <PathfinderMajorSearchSelect
          value={value || null}
          onChange={onChange}
          selectedLabel={value ? answerLabelMap?.[value] : null}
          placeholder={majorSearchPlaceholder}
          showDefaultSuggestions={isDegreeGoalType}
        />
      );

    case 'MULTI_SELECT':
    case 'MULTI_SELECT_OPTION':
      return (
        <PathfinderMultiSelectCardGrid
          options={toSelectOptions(resolvedOptions)}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          exclusiveOptionCode={config.exclusiveOption}
          layoutVariant={
            question.questionCode === 'Q_ROLE_TASKS' ||
            config.layoutVariant === 'task-grid'
              ? 'task-grid'
              : 'default'
          }
        />
      );

    case 'TEXT_INPUT':
      return (
        <PathfinderTextInput
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'SINGLE_SELECT_CARD':
      return (
        <PathfinderSelectCardGroup
          options={toSelectOptions(resolvedOptions)}
          value={value || null}
          onChange={(id) => onChange(id)}
        />
      );

    case 'SINGLE_SELECT':
    default:
      return (
        <SelectOption
          options={toSelectOptions(resolvedOptions)}
          value={value || null}
          onChange={(id) => onChange(id)}
          layoutVariant={
            VISA_GRID_QUESTION_CODES.has(question.questionCode) ||
            config.layoutVariant === 'visa-grid'
              ? 'visa-grid'
              : ROLE_GRID_QUESTION_CODES.has(question.questionCode) ||
                  config.layoutVariant === 'role-grid'
                ? 'role-grid'
                : 'default'
          }
        />
      );
  }
}

// ─── 이전 답변 요약 카드 ────────────────────────────────────────────────────
function PriorAnswerCard({ question, answerValue }) {
  const selected = question.options?.find((o) => o.optionCode === answerValue);
  if (!selected) return null;

  return (
    <div className='flex h-[76px] w-full items-center justify-center rounded-[15px] bg-surface-lightgray60 px-6 py-4'>
      <div className='flex flex-col items-center gap-[2px] text-center'>
        <span className='text-base font-medium leading-[1.5] text-green-deep02'>
          {selected.optionLabel}
        </span>
        {selected.description && (
          <span className='text-[13px] font-normal leading-[1.4] text-green-deep02'>
            {selected.description}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Pathfinder 메인 페이지 ─────────────────────────────────────────────────
const Pathfinder = () => {
  const [searchParams] = useSearchParams();
  const onboardingStartRef = useRef(Date.now());
  const { user, isLoggedIn, loading: authLoading, refreshUser } = useAuth();
  const { showLoginModal } = useLoginRequiredModal();
  const isMobile = useIsMobile();
  const isAdmin = ADMIN_ROLES.includes(String(user?.role ?? '').toUpperCase());

  const initialDraft = getPathfinderDraft();
  // step 0 = goal_type 선택, step 1+ = BE에서 불러온 동적 질문
  const [step, setStep] = useState(initialDraft.step ?? 0);
  const [sessionId, setSessionId] = useState(initialDraft.sessionId ?? null);
  const [questions, setQuestions] = useState(initialDraft.questions ?? []);
  // answers: { questionCode: value } — MULTI_SELECT는 배열
  const [answers, setAnswers] = useState(initialDraft.answers ?? {});
  const [answerLabels, setAnswerLabels] = useState(
    initialDraft.answerLabels ?? {},
  );
  // sectionAnswers: { sectionCode: { questionCode: value } } — 섹션별 누적
  const [sectionAnswers, setSectionAnswers] = useState(
    initialDraft.sectionAnswers ?? {},
  );
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(initialDraft.result ?? null);

  const totalSteps = 1 + questions.length;
  const questionWord = 'Question';
  const progressLabel =
    step === 0 && questions.length === 0
      ? `${questionWord} 1/–`
      : `${questionWord} ${step + 1}/${totalSteps}`;

  const currentQuestion = step === 0 ? GOAL_TYPE_QUESTION : questions[step - 1];
  const currentAnswer = answers[currentQuestion?.questionCode];
  const skipExpBlockDuration = EXP_BLOCK_DURATION_SKIP_GOAL_CODES.has(
    answers.Q_GOAL_TYPE,
  );

  const isExpBlockFlow = currentQuestion?.answerType === 'EXP_BLOCK_FLOW';

  const isAnswered = isQuestionAnswered(currentQuestion, currentAnswer);
  const isNextDisabled = loading || authLoading || !isAnswered;

  useEffect(() => {
    localStorage.setItem(
      PATHFINDER_DRAFT_STORAGE_KEY,
      JSON.stringify({
        step,
        sessionId,
        questions,
        answers,
        answerLabels,
        sectionAnswers,
        result,
      }),
    );
  }, [
    step,
    sessionId,
    questions,
    answers,
    answerLabels,
    sectionAnswers,
    result,
  ]);

  // ─── 답변 업데이트 ────────────────────────────────────────────────────────
  const handleChange = (val, meta = {}) => {
    const { questionCode, sectionCode } = currentQuestion;
    const shouldClearKoreanDegreeFollowUps =
      questionCode === 'Q_KR_DEGREE' && val === 'KR_DEG_NO';

    setAnswers((prev) => {
      const next = { ...prev, [questionCode]: val };

      if (shouldClearKoreanDegreeFollowUps) {
        KOREAN_DEGREE_FOLLOW_UP_CODES.forEach((code) => delete next[code]);
      }

      if (questionCode === 'Q_LANG_PURPOSE') {
        delete next.Q_LANG_USE_CONTEXT;
        delete next.Q_MAJOR_LINK;
        if (prev.Q_GOAL_TYPE === 'GOAL_LANGUAGE') {
          delete next.Q_JOB_ROLE;
        }
      }

      if (questionCode === 'Q_OPTIONAL_CAREER_LINK' && val !== 'CAREER_YES') {
        delete next.Q_ROLE_EXPLORATION;
      }

      return next;
    });

    if (shouldClearKoreanDegreeFollowUps) {
      setAnswerLabels((prev) => {
        const next = { ...prev };
        KOREAN_DEGREE_FOLLOW_UP_CODES.forEach((code) => delete next[code]);
        return next;
      });
    }

    if (Object.prototype.hasOwnProperty.call(meta, 'selectedOptions')) {
      setAnswerLabels((prev) => {
        const selectedOptions = meta.selectedOptions || [];
        if (selectedOptions.length === 0) {
          const next = { ...prev };
          delete next[questionCode];
          return next;
        }

        return {
          ...prev,
          [questionCode]: Object.fromEntries(
            selectedOptions.map((option) => [option.value, option.label]),
          ),
        };
      });
    }
    setSectionAnswers((prev) => {
      const nextSection = { ...(prev[sectionCode] || {}), [questionCode]: val };

      if (shouldClearKoreanDegreeFollowUps) {
        KOREAN_DEGREE_FOLLOW_UP_CODES.forEach(
          (code) => delete nextSection[code],
        );
      }

      if (questionCode === 'Q_LANG_PURPOSE') {
        delete nextSection.Q_LANG_USE_CONTEXT;
        delete nextSection.Q_MAJOR_LINK;
        delete nextSection.Q_JOB_ROLE;
      }

      if (questionCode === 'Q_OPTIONAL_CAREER_LINK' && val !== 'CAREER_YES') {
        delete nextSection.Q_ROLE_EXPLORATION;
      }

      return {
        ...prev,
        [sectionCode]: nextSection,
      };
    });
  };

  // ─── 질문 노출 제어 ───────────────────────────────────────────────────────
  // ROLE_etc(직무 Other) 선택 시 건너뛸 질문인지 판별
  const isHiddenQuestion = (question) => {
    const questionCode = question?.questionCode;
    const isLanguageGoal = answers.Q_GOAL_TYPE === 'GOAL_LANGUAGE';
    const langPurpose = answers.Q_LANG_PURPOSE;

    if (shouldHideKoreanDegreeFollowUp(questionCode, answers)) {
      return true;
    }

    if (questionCode === 'Q_ROLE_EXPLORATION') {
      return shouldHideRoleExploration(answers);
    }

    if (isLanguageGoal) {
      if (questionCode === 'Q_LANG_USE_CONTEXT') {
        return langPurpose === 'LANG_NONE';
      }

      if (questionCode === 'Q_MAJOR_LINK') {
        return langPurpose !== 'LANG_UNIVERSITY';
      }

      if (questionCode === 'Q_JOB_ROLE') {
        return langPurpose !== 'LANG_CAREER';
      }
    }

    return (
      answers.Q_JOB_ROLE === 'ROLE_etc' &&
      ROLE_ETC_SKIP_QUESTION_CODES.has(questionCode)
    );
  };

  // 현재 step 이후 노출할 다음 질문 step (없으면 questions.length + 1 → 설문 종료)
  const getNextVisibleStep = (fromStep) => {
    let next = fromStep + 1;
    while (next <= questions.length && isHiddenQuestion(questions[next - 1])) {
      next += 1;
    }
    return next;
  };

  // 현재 step 이전 노출할 질문 step (없으면 0 → goal_type)
  const getPrevVisibleStep = (fromStep) => {
    let prev = fromStep - 1;
    while (prev >= 1 && isHiddenQuestion(questions[prev - 1])) {
      prev -= 1;
    }
    return prev;
  };

  const handleNext = async (expBlockVal) => {
    if (loading || authLoading) return;
    if (!isLoggedIn) {
      showLoginModal('pathfinder_onboarding_next');
      return;
    }

    if (step === 0) {
      // Q1: 세션 생성 + goalType 기반 질문 목록 조회
      const selectedGoalCode = answers['Q_GOAL_TYPE'];
      const beGoalType = GOAL_TYPE_BE_MAP[selectedGoalCode];
      setLoading(true);
      try {
        const [session, fetchedQuestions] = await Promise.all([
          createPathfinderSession(beGoalType, 'PRE_ENTRY'),
          getPathfinderQuestions(beGoalType, 'PRE_ENTRY'),
        ]);
        setSessionId(session.id);
        // Q_GOAL_TYPE은 session 생성 시 이미 저장 → 목록에서 제외
        setQuestions(
          orderQuestionsByGoalType(
            fetchedQuestions.filter((q) => q.questionCode !== 'Q_GOAL_TYPE'),
            beGoalType,
          ),
        );
        setStep(1);
      } catch (err) {
        if (err?.response?.status === 401 && !window.__isLoggedIn) {
          showLoginModal('pathfinder_onboarding_401');
        } else {
          console.error('Session 생성 실패:', err);
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // Q2+: 현재 섹션 누적 데이터 저장 후 다음 스텝
    const { sectionCode, questionCode } = currentQuestion;
    // EXP_BLOCK_FLOW: onComplete에서 값을 직접 받아 stale 클로저 방지
    const finalAnswer = expBlockVal ?? currentAnswer;

    const isRoleEtcSkip =
      questionCode === 'Q_JOB_ROLE' && finalAnswer === 'ROLE_etc';
    if (isRoleEtcSkip) {
      setAnswers((prev) => ({ ...prev, Q_JOB_INDUSTRY: 'IND_etc' }));
      setSectionAnswers((prev) => ({
        ...prev,
        [sectionCode]: {
          ...(prev[sectionCode] || {}),
          Q_JOB_INDUSTRY: 'IND_etc',
        },
      }));
    }

    const payload = isRoleEtcSkip
      ? {
          ...(sectionAnswers[sectionCode] || {}),
          [questionCode]: finalAnswer,
          Q_JOB_INDUSTRY: 'IND_etc',
        }
      : isExpBlockFlow && expBlockVal
        ? {
            ...(sectionAnswers[sectionCode] || {}),
            [questionCode]: expBlockVal,
          }
        : sectionAnswers[sectionCode] || {};

    setLoading(true);
    try {
      if (
        isExpBlockFlow &&
        !skipExpBlockDuration &&
        finalAnswer?.experiences?.length > 0
      ) {
        await savePathfinderExperiences(sessionId, finalAnswer.experiences);
      }
      await savePathfinderSection(sessionId, sectionCode, payload);

      const isExpBlockNone =
        isExpBlockFlow &&
        (expBlockVal ?? finalAnswer)?.step2?.includes('EXP_NONE_TYPE');
      const nextStep = resolveNextQuestionStep({
        questions,
        fromStep: step,
        goalCode: answers.Q_GOAL_TYPE,
        isExpBlockNone,
        isHiddenQuestion,
      });

      if (nextStep > questions.length) {
        // 남은 질문 없음 → 점수 계산
        const evalResult = await evaluatePathfinderSession(sessionId);
        persistReadinessScore(evalResult);
        setResult(evalResult);
        refreshUser();

        const timeSpent = Math.round(
          (Date.now() - onboardingStartRef.current) / 1000,
        );
        trackPathfinderOnboardingComplete({
          path_type: evalResult?.pathType || 'job_seeker',
          current_step: evalResult?.currentStep || 'step_01',
          time_spent_sec: timeSpent,
          source: searchParams.get('source') || 'gnb_pathfinder',
        });
      } else {
        setStep(nextStep);
      }
    } catch (err) {
      if (err?.response?.status === 401 && !window.__isLoggedIn) {
        showLoginModal('pathfinder_onboarding_401');
      } else {
        console.error('섹션 저장 실패:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Back ─────────────────────────────────────────────────────────────────
  const handleBack = () => {
    if (step > 0) setStep(getPrevVisibleStep(step));
  };

  // ─── Retake: 점수·선택지 초기화 후 첫 질문부터 재진행 ───────────────────────
  const handleRetake = () => {
    clearPathfinderDraft();
    setStep(0);
    setSessionId(null);
    setQuestions([]);
    setAnswers({});
    setAnswerLabels({});
    setSectionAnswers({});
    setResult(null);
    onboardingStartRef.current = Date.now();
  };

  if (result) {
    return (
      <PathfinderResult
        result={result}
        goalType={GOAL_TYPE_BE_MAP[answers.Q_GOAL_TYPE]}
        isAdmin={isAdmin}
        onRetake={handleRetake}
      />
    );
  }

  if (!currentQuestion) return null;

  const useMobileTargetMajorFieldDesign =
    isMobile &&
    currentQuestion.questionCode === 'Q_TARGET_MAJOR_FIELD' &&
    answers.Q_GOAL_TYPE !== 'GOAL_JOB';
  const useMobileOptionalCareerLinkDesign =
    isMobile && currentQuestion.questionCode === 'Q_OPTIONAL_CAREER_LINK';
  const useMobileLanguageCareerRoleDesign =
    isMobile &&
    answers.Q_GOAL_TYPE === 'GOAL_LANGUAGE' &&
    answers.Q_LANG_PURPOSE === 'LANG_CAREER' &&
    currentQuestion.questionCode === 'Q_JOB_ROLE';
  const useMobileLanguageCareerContextDesign =
    isMobile &&
    answers.Q_GOAL_TYPE === 'GOAL_LANGUAGE' &&
    answers.Q_LANG_PURPOSE === 'LANG_CAREER' &&
    currentQuestion.questionCode === 'Q_LANG_USE_CONTEXT';
  const mobileQuestionTitle = useMobileTargetMajorFieldDesign
    ? 'What major are you interested in studying?'
    : useMobileLanguageCareerContextDesign
      ? 'Where will you use Korean?'
      : useMobileLanguageCareerRoleDesign
        ? 'Which job role is Korean related to?'
        : currentQuestion.prompt;
  const mobileQuestionHelperText = useMobileTargetMajorFieldDesign
    ? 'Search and select your primary major'
    : useMobileOptionalCareerLinkDesign
      ? "We'll suggest career paths and learning modules."
      : useMobileLanguageCareerContextDesign
        ? 'Select all situations where you plan to use Korean.'
        : useMobileLanguageCareerRoleDesign
          ? 'Select the role where you plan to use Korean.'
          : currentQuestion.helperText;

  if (isMobile && isExpBlockFlow) {
    const expConfig = parseConfig(currentQuestion.configJson);
    const isTimelineFlow = !expConfig.step2Options?.length;

    if (isTimelineFlow) {
      return (
        <PathfinderMobileTimelineFlow
          value={currentAnswer}
          onChange={handleChange}
          onComplete={handleNext}
          onBack={step > 0 ? handleBack : undefined}
          config={expConfig}
          options={currentQuestion.options || []}
          progressLabel={progressLabel}
          questionTitle={currentQuestion.prompt}
          questionHelperText={currentQuestion.helperText}
          loading={loading}
        />
      );
    }

    if (!isTimelineFlow) {
      return (
        <PathfinderMobileExpBlockFlow
          value={currentAnswer}
          onChange={handleChange}
          onComplete={handleNext}
          onBack={step > 0 ? handleBack : undefined}
          config={expConfig}
          options={currentQuestion.options || []}
          progressLabel={progressLabel}
          loading={loading}
          skipDurationStep={skipExpBlockDuration}
        />
      );
    }
  }

  if (isMobile) {
    return (
      <PathfinderMobileQuestionLayout
        progressLabel={progressLabel}
        title={mobileQuestionTitle}
        helperText={mobileQuestionHelperText}
        onBack={step > 0 ? handleBack : undefined}
        onNext={handleNext}
        nextDisabled={isNextDisabled}
      >
        <PathfinderMobileQuestionInput
          key={currentQuestion.questionCode}
          question={currentQuestion}
          value={currentAnswer}
          onChange={handleChange}
          answers={answers}
          answerLabelMap={answerLabels[currentQuestion.questionCode]}
        />
      </PathfinderMobileQuestionLayout>
    );
  }

  // EXP_BLOCK_FLOW: 자체 헤더 + 툴바를 포함한 전용 렌더
  if (isExpBlockFlow) {
    const expConfig = parseConfig(currentQuestion.configJson);
    // step2Options가 없으면 타임라인 모드 (Q_TIMELINE_DEFINED)
    const isTimelineFlow = !expConfig.step2Options?.length;

    if (isTimelineFlow) {
      return (
        <PathfinderTimelineFlow
          value={currentAnswer}
          onChange={handleChange}
          onComplete={handleNext}
          onBack={step > 0 ? handleBack : undefined}
          config={expConfig}
          options={currentQuestion.options || []}
          progressLabel={progressLabel}
          loading={loading}
        />
      );
    }

    return (
      <PathfinderExpBlockFlow
        value={currentAnswer}
        onChange={handleChange}
        onComplete={handleNext}
        onBack={step > 0 ? handleBack : undefined}
        config={expConfig}
        options={currentQuestion.options || []}
        progressLabel={progressLabel}
        loading={loading}
        skipDurationStep={skipExpBlockDuration}
      />
    );
  }

  const config = parseConfig(currentQuestion.configJson);
  const priorQuestionCodes = config.priorQuestions || [];
  const priorQuestions = priorQuestionCodes
    .map((code) => questions.find((q) => q.questionCode === code))
    .filter(Boolean);

  return (
    <div className='flex min-h-[calc(100vh-74px)] flex-col bg-grayscale-gray00'>
      <div className='flex flex-1 items-center justify-center px-20 py-20'>
        <div className='flex w-full max-w-[868px] flex-1 flex-col items-start justify-center self-stretch overflow-visible'>
          <PathfinderQuestionHeader
            progressLabel={progressLabel}
            title={currentQuestion.prompt}
            helperText={currentQuestion.helperText}
          />

          {priorQuestions.length > 0 && (
            <div className='mb-4 flex w-full flex-col gap-3'>
              {priorQuestions.map((q) => (
                <PriorAnswerCard
                  key={q.questionCode}
                  question={q}
                  answerValue={answers[q.questionCode]}
                />
              ))}
            </div>
          )}

          <QuestionInput
            key={currentQuestion.questionCode}
            question={currentQuestion}
            value={currentAnswer}
            onChange={handleChange}
            answers={answers}
            answerLabelMap={answerLabels[currentQuestion.questionCode]}
          />
        </div>
      </div>

      <PathfinderBottomToolbar
        onBack={step > 0 ? handleBack : undefined}
        onNext={handleNext}
        nextDisabled={isNextDisabled}
      />
    </div>
  );
};

export default Pathfinder;
